import { useEffect, useRef, useState } from 'react';
import { Head } from '@unhead/react';
import { trackEvent } from '../analytics/track';

const APPLE_URL = "https://apps.apple.com/us/app/satstacker-stack-sats-easier/id6748700654";
const GOOGLE_URL = "https://play.google.com/store/apps/details?id=com.satstacker.app";
const YOUTUBE_ID = "UEeW0RH5_Hk";
const MOBILEAPPDAILY_URL = "https://www.mobileappdaily.com/product-review/satstacker";
const ILOUNGE_URL = "https://www.ilounge.com/review/apps/satstacker";

const publications = [
  { name: "MobileAppDaily", color: "#111", bg: "#fff",     url: "https://www.mobileappdaily.com/product-review/satstacker" },
  { name: "iLounge",        color: "#fff", bg: "#E8450A",  url: "https://www.ilounge.com/review/apps/satstacker" },
  { name: "Tapscape",       color: "#fff", bg: "#2563eb",  url: "https://www.tapscape.com/satstacker/" },
  { name: "iPhoneGlance",   color: "#fff", bg: "#555",     url: "https://www.iphoneglance.com/2026/03/15/review-satstacker-pro-smarter-bitcoin-stacking-on-autopilot/" },
  { name: "fanAppic",       color: "#fff", bg: "#7c3aed",  url: "https://fanappic.com/satstacker-pro-review-a-smarter-way-to-stack-bitcoin-automatically/" },
  { name: "TechBullion",    color: "#fff", bg: "#1a1a2e",  url: "https://techbullion.com/satstacker-helps-you-refine-your-bitcoin-dca-strategy-with-structured-timing-and-automation/" },
  { name: "USAWIRE",        color: "#fff", bg: "#b91c1c",  url: "https://usawire.com/bitcoin-dca-how-satstacker-applies-smart-timing-to-refine/amp/" },
];

const features = [
  {
    icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F7931A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
    title: "Smart Timing",
    desc: "Analyzes volatility and looks for lower prices within each buy window before executing.",
  },
  {
    icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F7931A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`,
    title: "Non-Custodial",
    desc: "Uses your connected Strike account. SatStacker never holds your cash or Bitcoin.",
  },
  {
    icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F7931A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>`,
    title: "Fully Automated",
    desc: "Set a daily, weekly, or bi-weekly plan and walk away. No charts. No FOMO. No guessing.",
  },
  {
    icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F7931A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>`,
    title: "Live Dashboard",
    desc: "Track stacked sats, buy history, and active Smart Timing windows in one clean view.",
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function Section({ children, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
      }}
    >
      {children}
    </div>
  );
}

const MOBILE_QUOTES = [
  { quote: "The smartest way to stack Bitcoin on autopilot.", source: "MobileAppDaily" },
  { quote: "The smarter way to invest in Bitcoin.", source: "iLounge" },
  { quote: "Structure over speculation.", source: "WebAppRater" },
  { quote: "A game-changer for long-term holders.", source: "MobileAppDaily" },
  { quote: "A focused execution companion, not a speculative platform.", source: "USAWIRE" },
  { quote: "Optimize accumulation without watching the market.", source: "iPhoneGlance" },
  { quote: "Refines DCA without abandoning its core principle of consistency.", source: "TechBullion" },
  { quote: "A compelling and simple tool for long-term stackers.", source: "fanAppic" },
];

function MobileQuote() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(i => (i + 1) % MOBILE_QUOTES.length);
        setVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const item = MOBILE_QUOTES[index];
  return (
    <div style={{
      textAlign: "center",
      opacity: visible ? 1 : 0,
      transition: "opacity 0.4s ease",
    }}>
      <span style={{
        fontFamily: "Georgia, serif", fontSize: 12.5, fontStyle: "italic",
        color: "rgba(240,237,232,0.65)", letterSpacing: "0.01em",
      }}>"{item.quote}" </span>
      <span style={{
        fontFamily: "DM Sans, sans-serif", fontSize: 10, fontWeight: 600,
        letterSpacing: "0.14em", textTransform: "uppercase", color: "#F7931A",
      }}>— {item.source}</span>
    </div>
  );
}

export default function MarketingLanding() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const trackStoreClick = (store, placement) => {
    trackEvent("marketing_store_clicked", {
      store,
      placement,
    });
  };

  const startDesktopSmartTiming = () => {
    // Web checkout is currently desktop-width only.
    if (isMobile) return;

    trackEvent("marketing_web_trial_clicked", {
      placement: "marketing_home",
      product: "smart_timing_monthly_web",
      trial: true,
      price_after_trial: 1.99,
    });

    try {
      localStorage.setItem(
        "satstacker_signup_intent",
        JSON.stringify({
          intent: "smart_timing_trial",
          source: "marketing_home_desktop",
          return_to: "/plans?smartTiming=1",
          ts: Date.now(),
        })
      );
    } catch {}

    window.location.href =
      `/signup?redirect=${encodeURIComponent("/plans?smartTiming=1")}`;
  };

  const title =
    "Bitcoin DCA App & Automated Bitcoin Buying | SatStacker";

  const description =
    "Automate your Bitcoin DCA with SatStacker. Set recurring Bitcoin buys, connect Strike securely, and use Smart Timing to look for better prices within each buy window.";

  const canonical = "https://satstacker.app/";

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "SatStacker",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web, iOS, Android",
    url: canonical,
    description,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };

  return (
    <>
      <Head>
        <title>{title}</title>

        <meta
          name="description"
          content={description}
        />

        <link
          rel="canonical"
          href={canonical}
        />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="SatStacker" />
        <meta
          property="og:image"
          content="https://satstacker.app/icons/icon-192.png"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta name="twitter:title" content={title} />
        <meta
          name="twitter:description"
          content={description}
        />
        <meta
          name="twitter:image"
          content="https://satstacker.app/icons/icon-192.png"
        />

        <script type="application/ld+json">
          {JSON.stringify(softwareSchema)}
        </script>
      </Head>

      <div
        style={{
          background: "#0a0a0a",
          color: "#f0ede8",
          fontFamily: "'Georgia', 'Times New Roman', serif",
          minHeight: "100vh",
          overflowX: "hidden",
          maxWidth: "100vw",
        }}
      >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .hero-font { font-family: 'Space Grotesk', 'DM Sans', sans-serif; }
        .body-font { font-family: 'DM Sans', sans-serif; }

        .gold { color: #F7931A; }
        .gold-bg { background: #F7931A; }

        .btn-primary {
          background: #F7931A;
          color: #0a0a0a;
          border: none;
          padding: 14px 32px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 700;
          font-size: 15px;
          border-radius: 10px;
          cursor: pointer;
          text-decoration: none;
          display: inline-block;
          letter-spacing: 0.02em;
          transition: background 0.2s, transform 0.15s;
        }
        .btn-primary:hover { background: #fb8a2f; transform: translateY(-1px); }

        .btn-outline {
          background: transparent;
          color: #f0ede8;
          border: 1px solid rgba(240,237,232,0.3);
          padding: 14px 32px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 400;
          font-size: 15px;
          border-radius: 4px;
          cursor: pointer;
          text-decoration: none;
          display: inline-block;
          letter-spacing: 0.02em;
          transition: border-color 0.2s, transform 0.15s;
        }
        .btn-outline:hover { border-color: #F7931A; color: #F7931A; transform: translateY(-1px); }

        .store-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 10px;
          padding: 12px 20px;
          text-decoration: none;
          color: #f0ede8;
          font-family: 'DM Sans', sans-serif;
          transition: background 0.2s, border-color 0.2s;
          min-width: 160px;
        }
        .store-btn:hover { background: rgba(247,147,26,0.1); border-color: rgba(247,147,26,0.4); }
        .store-btn-label { font-size: 10px; opacity: 0.6; display: block; line-height: 1; margin-bottom: 2px; font-family: 'DM Sans', sans-serif; }
        .store-btn-name { font-size: 15px; font-weight: 500; display: block; line-height: 1.2; }

        .feature-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 12px;
          padding: 32px 28px;
          transition: border-color 0.3s, background 0.3s;
        }
        .feature-card:hover { border-color: rgba(247,147,26,0.3); background: rgba(247,147,26,0.04); }

        .pub-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 18px;
          border-radius: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
          letter-spacing: 0.01em;
        }

        .divider {
          width: 60px;
          height: 2px;
          background: #F7931A;
          margin: 0 auto 24px;
        }

        .star { color: #F7931A; font-size: 20px; }

        .hero-video {
          transform: translateY(-28px);
        }

        .noise-overlay {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          opacity: 0.025;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
        }

        @media (max-width: 768px) {
          .hero-grid { flex-direction: column !important; gap: 40px !important; }
          .hero-video { transform: none !important; width: 100%; }
          .features-grid { grid-template-columns: 1fr !important; }
          .hero-title { font-size: 2.8rem !important; }
          .ilounge-card { padding: 32px 20px !important; }
          .cta-card { padding: 48px 20px !important; }
        }
      `}</style>

      <div className="noise-overlay" />

      {/* NAV */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: "16px 24px",
        display: "flex", alignItems: "center",
        background: "rgba(9,9,11,0.88)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <img
            src="/assets/logo_full.png"
            alt="SatStacker"
            style={{ height: 32 }}
            onError={e => { e.target.style.display = "none"; }}
          />
          <span className="hero-font" style={{ fontSize: 20, fontWeight: 700, color: "#f4f4f5" }}>
            SatStacker
          </span>
        </a>

        <div
          className="body-font"
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 14 : 18,
            fontSize: 14,
          }}
        >
          {!isMobile && (
            <>
              <a href="/bitcoin-dca" style={{ color: "rgba(244,244,245,0.68)", textDecoration: "none" }}>
                Bitcoin DCA Guide
              </a>
              <a href="/calculator" style={{ color: "rgba(244,244,245,0.68)", textDecoration: "none" }}>
                DCA Calculator
              </a>
              <a href="/pricing" style={{ color: "rgba(244,244,245,0.68)", textDecoration: "none" }}>
                Pricing
              </a>
            </>
          )}

          <a
            href="/login"
            style={{
              color: "rgba(244,244,245,0.86)",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            Log in
          </a>

          {!isMobile && (
            <button
              type="button"
              onClick={startDesktopSmartTiming}
              className="btn-primary"
              style={{
                padding: "10px 16px",
                fontSize: 13,
              }}
            >
              Start free month
            </button>
          )}
        </div>
      </nav>

      {/* Spacer for fixed nav */}
      <div style={{ height: 68 }} />

      {/* QUOTE TICKER — scrolling on desktop, rotating on mobile */}
      <div style={{
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        background: "rgba(10,10,10,0.95)",
        padding: "16px 0",
      }}>
        {isMobile ? (
          <div style={{ padding: "0 28px", minHeight: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MobileQuote />
          </div>
        ) : (
          <div style={{ overflow: "hidden", position: "relative" }}>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 80, background: "linear-gradient(to right, rgba(10,10,10,0.95), transparent)", zIndex: 2, pointerEvents: "none" }} />
          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 80, background: "linear-gradient(to left, rgba(10,10,10,0.95), transparent)", zIndex: 2, pointerEvents: "none" }} />
          <style>{`
            @keyframes ticker {
              0%   { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .ticker-track {
              display: flex;
              width: max-content;
              animation: ticker 120s linear infinite;
            }
            .ticker-track:hover { animation-play-state: paused; }
            .ticker-item {
              display: flex;
              align-items: center;
              gap: 10px;
              padding: 0 44px;
              white-space: nowrap;
              flex-shrink: 0;
            }
            .ticker-quote {
              font-family: 'Georgia', serif;
              font-size: 12.5px;
              font-style: italic;
              color: rgba(240,237,232,0.65);
              letter-spacing: 0.01em;
            }
            .ticker-source {
              font-family: 'DM Sans', sans-serif;
              font-size: 10px;
              font-weight: 600;
              letter-spacing: 0.14em;
              text-transform: uppercase;
              color: #F7931A;
              flex-shrink: 0;
            }
            .ticker-dot {
              color: rgba(247,147,26,0.3);
              font-size: 8px;
              flex-shrink: 0;
            }
          `}</style>
          <div className="ticker-track">
            {[
              { quote: "Quietly upgrading how people buy Bitcoin.", source: "MobileAppDaily" },
              { quote: "The smartest way to stack Bitcoin on autopilot.", source: "MobileAppDaily" },
              { quote: "A game-changer for long-term holders.", source: "MobileAppDaily" },
              { quote: "The smarter way to invest in Bitcoin.", source: "iLounge" },
              { quote: "Smart automation eliminates the need to guess.", source: "iLounge" },
              { quote: "More bang for your buck.", source: "Tapscape" },
              { quote: "Reduces the risk of human error.", source: "Tapscape" },
              { quote: "A focused execution companion, not a speculative platform.", source: "USAWIRE" },
              { quote: "Refines DCA without abandoning its core principle of consistency.", source: "TechBullion" },
              { quote: "Optimize accumulation without watching the market.", source: "iPhoneGlance" },
              { quote: "A compelling and simple tool for long-term stackers.", source: "fanAppic" },
              { quote: "Structure over speculation.", source: "WebAppRater" },
              { quote: "Quietly upgrading how people buy Bitcoin.", source: "MobileAppDaily" },
              { quote: "The smartest way to stack Bitcoin on autopilot.", source: "MobileAppDaily" },
              { quote: "A game-changer for long-term holders.", source: "MobileAppDaily" },
              { quote: "The smarter way to invest in Bitcoin.", source: "iLounge" },
              { quote: "Smart automation eliminates the need to guess.", source: "iLounge" },
              { quote: "More bang for your buck.", source: "Tapscape" },
              { quote: "Reduces the risk of human error.", source: "Tapscape" },
              { quote: "A focused execution companion, not a speculative platform.", source: "USAWIRE" },
              { quote: "Refines DCA without abandoning its core principle of consistency.", source: "TechBullion" },
              { quote: "Optimize accumulation without watching the market.", source: "iPhoneGlance" },
              { quote: "A compelling and simple tool for long-term stackers.", source: "fanAppic" },
              { quote: "Structure over speculation.", source: "WebAppRater" },
            ].map((item, i) => (
              <div key={i} className="ticker-item">
                <span className="ticker-quote">"{item.quote}"</span>
                <span className="ticker-source">— {item.source}</span>
                <span className="ticker-dot">●</span>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
      {/* HERO */}
      <section style={{
        minHeight: "auto",
        display: "flex", alignItems: "center",
        padding: "40px 24px 56px",
        maxWidth: 1280, margin: "0 auto",
        position: "relative",
        boxSizing: "border-box",
        width: "100%",
      }}>
        {/* background glow */}
        <div style={{
          position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)",
          width: 600, height: 600,
          background: "radial-gradient(circle, rgba(247,147,26,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />

        <div className="hero-grid" style={{
          display: "flex", alignItems: "center", gap: 64, width: "100%",
          opacity: heroVisible ? 1 : 0,
          transform: heroVisible ? "translateY(0)" : "translateY(24px)",
          transition: "opacity 0.9s ease, transform 0.9s ease",
        }}>
          <div style={{ flex: 1.15 }}>
            <div className="body-font" style={{
              fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase",
              color: "#F7931A", marginBottom: 20, fontWeight: 500,
            }}>
              Automated Bitcoin DCA
            </div>
            <h1
              className="hero-font hero-title"
              style={{
                fontSize: "3.8rem",
                lineHeight: 1.1,
                fontWeight: 900,
                marginBottom: 28,
                color: "#f0ede8",
              }}
            >
              Smarter Bitcoin DCA.<br />
              <span className="gold">Stack More Sats.</span>
            </h1>
            <p className="body-font" style={{
              fontSize: 18, lineHeight: 1.7, color: "rgba(240,237,232,0.65)",
              maxWidth: 480, marginBottom: 18, fontWeight: 300,
            }}>
              SatStacker connects to your existing Strike account and looks for better Bitcoin prices within each buy window, automatically.
            </p>

            <p className="body-font" style={{
              fontSize: 14,
              lineHeight: 1.5,
              color: "rgba(244,244,245,0.82)",
              marginBottom: 8,
              fontWeight: 500,
            }}>
              Uses your Strike account · SatStacker never holds your funds
            </p>

            <a
              href="/strike-bitcoin-dca"
              className="body-font"
              style={{
                display: "inline-block",
                color: "#F7931A",
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
                marginBottom: 26,
              }}
            >
              Learn how SatStacker works with Strike →
            </a>

            {isMobile ? (
              // MOBILE: preserve the existing experience exactly
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <a
                  href={APPLE_URL}
                  onClick={() => trackStoreClick("ios", "hero")}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "inline-block" }}
                >
                  <img
                    src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    style={{ height: 44 }}
                  />
                </a>

                <a
                  href={GOOGLE_URL}
                  onClick={() => trackStoreClick("android", "hero")}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "inline-block" }}
                >
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    style={{ height: 60, marginTop: -8 }}
                  />
                </a>
              </div>
            ) : (
              // DESKTOP ONLY: web signup becomes the primary CTA
              <div>
                <button
                  type="button"
                  onClick={startDesktopSmartTiming}
                  className="btn-primary"
                  style={{
                    padding: "15px 34px",
                    fontSize: 16,
                    fontWeight: 700,
                    marginBottom: 12,
                  }}
                >
                  Start your free month →
                </button>

                <p
                  className="body-font"
                  style={{
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: "rgba(240,237,232,0.5)",
                    marginBottom: 22,
                  }}
                >
                  $0 today · 1 month free · then $1.99/month · cancel anytime
                </p>

                <p
                  className="body-font"
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "rgba(240,237,232,0.35)",
                    marginBottom: 10,
                  }}
                >
                  Prefer the mobile app?
                </p>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <a
                    href={APPLE_URL}
                    onClick={() => trackStoreClick("ios", "hero_secondary")}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "inline-block" }}
                  >
                    <img
                      src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
                      alt="Download on the App Store"
                      style={{ height: 38 }}
                    />
                  </a>

                  <a
                    href={GOOGLE_URL}
                    onClick={() => trackStoreClick("android", "hero_secondary")}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "inline-block" }}
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      style={{ height: 52, marginTop: -7 }}
                    />
                  </a>
                </div>
              </div>
            )}

            {/* Rating — supporting proof after the primary action */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 26 }}>
              <div>
                {"★★★★".split("").map((s, i) => <span key={i} className="star">{s}</span>)}
                <span className="star">✬</span>
              </div>
              <span className="body-font" style={{ fontSize: 14, color: "rgba(240,237,232,0.5)" }}>
                4.5/5 — MobileAppDaily Certified
              </span>
            </div>
          </div>

          {/* Video */}
          <div
            className="hero-video"
            style={{ flex: 0.95, maxWidth: 520 }}
          >
            <div style={{
              borderRadius: 16,
              overflow: "hidden",
              border: "1px solid rgba(247,147,26,0.2)",
              boxShadow: "0 0 80px rgba(247,147,26,0.08)",
              aspectRatio: "16/9",
              background: "#111",
            }}>
              <iframe
                width="100%" height="100%"
                src={`https://www.youtube.com/embed/${YOUTUBE_ID}?rel=0&modestbranding=1`}
                title="SatStacker Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ display: "block" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* AS SEEN IN */}
      <Section>
        <div style={{
          borderTop: "1px solid rgba(255,255,255,0.06)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          padding: "48px 24px",
          background: "rgba(255,255,255,0.015)",
        }}>
          <p className="body-font" style={{
            textAlign: "center", fontSize: 11, letterSpacing: "0.2em",
            textTransform: "uppercase", color: "rgba(240,237,232,0.35)",
            marginBottom: 28, fontWeight: 500,
          }}>As Seen In</p>
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 12,
            justifyContent: "center", alignItems: "center",
          }}>
            {publications.map(p => (
              <a key={p.name} href={p.url} target="_blank" rel="noreferrer"
                className="pub-chip" style={{ background: p.bg, color: p.color, textDecoration: "none", opacity: 0.9, transition: "opacity 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                onMouseLeave={e => e.currentTarget.style.opacity = "0.9"}
              >
                {p.name}
              </a>
            ))}
          </div>
        </div>
      </Section>

      {/* REVIEW CALLOUT */}
      <Section>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "80px 24px", textAlign: "center" }}>
          <div className="divider" />
          <blockquote className="hero-font" style={{
            fontSize: "2.2rem", lineHeight: 1.3, fontWeight: 700,
            color: "#f0ede8", marginBottom: 24, fontStyle: "italic",
          }}>
            "The Smartest Way to Stack Bitcoin on Autopilot."
          </blockquote>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginBottom: 40 }}>
            <div>
              {"★★★★".split("").map((s, i) => <span key={i} className="star" style={{ fontSize: 24 }}>{s}</span>)}
              <span className="star" style={{ fontSize: 24 }}>✬</span>
            </div>
            <span className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 15 }}>
              MobileAppDaily · Certified Rating 4.5 / 5
            </span>
          </div>
          <a href={MOBILEAPPDAILY_URL} target="_blank" rel="noreferrer" className="btn-outline">
            Read Full Review →
          </a>
        </div>
      </Section>

      {/* FEATURES */}
      <Section>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 80px" }}>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div className="divider" />
            <h2 className="hero-font" style={{ fontSize: "2.4rem", fontWeight: 700, marginBottom: 12 }}>
              Built for long-term Bitcoiners
            </h2>
            <p className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 16, fontWeight: 300 }}>
              No trading. No leverage. Just disciplined, automated accumulation.
            </p>
          </div>
          <div className="features-grid" style={{
            display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20,
          }}>
            {features.map(f => (
              <div key={f.title} className="feature-card">
                <div style={{ marginBottom: 18, width: 24, height: 24 }} dangerouslySetInnerHTML={{ __html: f.icon }} />
                <h3 className="hero-font" style={{ fontSize: "1.3rem", fontWeight: 700, marginBottom: 10, color: "#f0ede8" }}>
                  {f.title}
                </h3>
                <p className="body-font" style={{ color: "rgba(240,237,232,0.55)", lineHeight: 1.65, fontWeight: 300, fontSize: 15 }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ILOUNGE REVIEW */}
      <Section>
        <div style={{
          maxWidth: 800, margin: "0 auto", padding: "0 24px 80px", textAlign: "center",
        }}>
          <div style={{
            background: "rgba(232,69,10,0.08)",
            border: "1px solid rgba(232,69,10,0.25)",
            borderRadius: 16,
            padding: "48px 24px",
          }}>
            <div className="pub-chip" style={{ background: "#E8450A", color: "#fff", marginBottom: 24, fontSize: 14 }}>
              iLounge
            </div>
            <blockquote className="hero-font" style={{
              fontSize: "1.7rem", lineHeight: 1.35, fontStyle: "italic",
              color: "#f0ede8", marginBottom: 20,
            }}>
              "The Smarter Way to Invest in Bitcoin"
            </blockquote>
            <a href={ILOUNGE_URL} target="_blank" rel="noreferrer" className="body-font" style={{
              color: "#E8450A", fontSize: 14, textDecoration: "none", letterSpacing: "0.05em",
            }}>
              Read Article →
            </a>
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section>
        <div style={{ maxWidth: 700, margin: "0 auto", padding: "0 24px 80px", textAlign: "center" }}>
          <div className="divider" />
          <h2 className="hero-font" style={{ fontSize: "2.2rem", fontWeight: 700, marginBottom: 48 }}>
            Three steps. Then forget it.
          </h2>
          {[
            { n: "01", t: "Connect Strike", d: "Securely link your Strike account via OAuth. SatStacker never touches your funds." },
            { n: "02", t: "Set your plan", d: "Choose daily, weekly, or bi-weekly. Set your budget. Pick basic or smart timing." },
            { n: "03", t: "Stack automatically", d: "SatStacker looks for better prices within each buy window and executes according to your recurring plan." },
          ].map((s, i) => (
            <div key={s.n} style={{
              display: "flex", gap: 28, textAlign: "left", marginBottom: 36,
              paddingBottom: 36,
              borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.06)" : "none",
            }}>
              <div className="hero-font gold" style={{ fontSize: "2rem", fontWeight: 900, minWidth: 48, lineHeight: 1 }}>
                {s.n}
              </div>
              <div>
                <h3 className="hero-font" style={{ fontSize: "1.2rem", fontWeight: 700, marginBottom: 8 }}>{s.t}</h3>
                <p className="body-font" style={{ color: "rgba(240,237,232,0.55)", lineHeight: 1.65, fontWeight: 300, fontSize: 15 }}>{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* LEARN BITCOIN DCA — contextual internal links for users + search */}
      <Section>
        <div style={{
          maxWidth: 1100, margin: "0 auto", padding: "0 24px 80px",
        }}>
          <div style={{ textAlign: "center", marginBottom: 34 }}>
            <div className="divider" />
            <h2 className="hero-font" style={{
              fontSize: "2.2rem", fontWeight: 700, marginBottom: 12,
            }}>
              Learn Bitcoin DCA
            </h2>
            <p className="body-font" style={{
              color: "rgba(240,237,232,0.52)",
              fontSize: 15,
              lineHeight: 1.7,
              maxWidth: 620,
              margin: "0 auto",
              fontWeight: 300,
            }}>
              Understand recurring Bitcoin buys, see how Smart DCA differs from a fixed schedule,
              or test your own cadence with historical price data.
            </p>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
          }}>
            {[
              {
                href: "/bitcoin-dca",
                eyebrow: "Guide",
                title: "How to DCA Bitcoin",
                body: "Learn how dollar-cost averaging works, how to choose a schedule, and how recurring Bitcoin buying can be automated.",
              },
              {
                href: "/smart-dca",
                eyebrow: "Smart DCA",
                title: "How Smart Timing Works",
                body: "See how Smart DCA keeps a recurring budget while adding execution rules inside each scheduled buy window.",
              },
              {
                href: "/calculator",
                eyebrow: "Tool",
                title: "Bitcoin DCA Calculator",
                body: "Compare scheduled DCA with historical Smart Timing results using your own amount and cadence.",
              },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                style={{
                  display: "block",
                  padding: 22,
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.025)",
                  textDecoration: "none",
                }}
              >
                <div className="body-font" style={{
                  color: "#F7931A",
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  marginBottom: 9,
                }}>
                  {item.eyebrow}
                </div>
                <h3 className="hero-font" style={{
                  color: "#f0ede8",
                  fontSize: "1.12rem",
                  fontWeight: 700,
                  marginBottom: 8,
                }}>
                  {item.title}
                </h3>
                <p className="body-font" style={{
                  color: "rgba(240,237,232,0.48)",
                  fontSize: 13.5,
                  lineHeight: 1.65,
                  fontWeight: 300,
                }}>
                  {item.body}
                </p>
              </a>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <div style={{
          maxWidth: 1200, margin: "0 auto", padding: "0 24px 80px",
        }}>
          <div style={{
            background: "linear-gradient(135deg, rgba(249,115,22,0.12) 0%, rgba(249,115,22,0.04) 100%)",
            border: "1px solid rgba(249,115,22,0.22)",
            borderRadius: 20,
            padding: "72px 32px",
            textAlign: "center",
          }}>
            <h2 className="hero-font" style={{ fontSize: "2.8rem", fontWeight: 700, marginBottom: 16 }}>
              Start stacking smarter.
            </h2>
            <p className="body-font" style={{
              color: "rgba(244,244,245,0.62)", fontSize: 17,
              fontWeight: 300, maxWidth: 520, margin: "0 auto 30px",
            }}>
              Connect your Strike account and let Smart Timing look for better prices inside each buy window.
            </p>

            {isMobile ? (
              <>
                <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
                  <a
                    href={APPLE_URL}
                    onClick={() => trackStoreClick("ios", "footer_cta")}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "inline-block" }}
                  >
                    <img
                      src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
                      alt="Download on the App Store"
                      style={{ height: 44 }}
                    />
                  </a>
                  <a
                    href={GOOGLE_URL}
                    onClick={() => trackStoreClick("android", "footer_cta")}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "inline-block" }}
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      style={{ height: 60, marginTop: -8 }}
                    />
                  </a>
                </div>
                <p className="body-font" style={{
                  marginTop: 20, fontSize: 13, color: "rgba(244,244,245,0.42)", fontWeight: 300,
                }}>
                  Available for iPhone and Android
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={startDesktopSmartTiming}
                  style={{ padding: "16px 28px", fontSize: 16 }}
                >
                  Start your free month →
                </button>
                <p className="body-font" style={{
                  marginTop: 14, fontSize: 13, color: "rgba(244,244,245,0.50)", fontWeight: 300,
                }}>
                  $0 today · 1 month free · then $1.99/month · cancel anytime
                </p>
              </>
            )}

            <p className="body-font" style={{
              marginTop: 24, fontSize: 13, color: "rgba(244,244,245,0.34)", fontWeight: 300,
            }}>
              Requires a Strike account · Non-custodial · Bitcoin only
            </p>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer style={{
        borderTop: "1px solid rgba(255,255,255,0.06)",
        padding: "48px 24px 32px",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* Top row */}
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 40, marginBottom: 40 }}>
            {/* Brand */}
            <div>
              <a href="/" style={{ textDecoration: "none" }}>
                <span className="hero-font" style={{ fontSize: 20, fontWeight: 700, color: "#f0ede8" }}>SatStacker</span>
              </a>
              <p className="body-font" style={{ fontSize: 13, color: "rgba(240,237,232,0.35)", fontWeight: 300, marginTop: 8, maxWidth: 220, lineHeight: 1.6 }}>
                Automated Bitcoin DCA with Smart Timing. Built to seek better entries within each buy window.
              </p>
            </div>
            {/* Links */}
            <div style={{ display: "flex", gap: 48, flexWrap: "wrap" }}>
              <div>
                <p className="body-font" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(240,237,232,0.3)", marginBottom: 12, fontWeight: 500 }}>Product</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <a
                    href="/product"
                    className="body-font"
                    style={{
                      color: "rgba(240,237,232,0.5)",
                      fontSize: 13,
                      textDecoration: "none",
                    }}
                  >
                    Product Reference
                  </a>
                  <a href="/calculator" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>Calculator</a>
                  <a href="/pricing" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>Pricing</a>
                  <a href="/download" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>Download</a>
                  <a href="/faq" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>FAQ</a>
                </div>
              </div>
              <div>
                <p className="body-font" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(240,237,232,0.3)", marginBottom: 12, fontWeight: 500 }}>Learn</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <a href="/bitcoin-dca" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>How to DCA Bitcoin</a>
                  <a href="/smart-dca" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>Smart DCA Guide</a>
                  <a href="/articles/dca-vs-lump-sum" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>Bitcoin DCA vs Lump Sum</a>
                  <a href="/articles/self-custody" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>Bitcoin Self-Custody</a>
                  <a href="/blog" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>Bitcoin DCA Blog</a>
                </div>
              </div>
              <div>
                <p className="body-font" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(240,237,232,0.3)", marginBottom: 12, fontWeight: 500 }}>Download</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <a href={APPLE_URL} target="_blank" rel="noreferrer" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>App Store (iOS)</a>
                  <a href={GOOGLE_URL} target="_blank" rel="noreferrer" className="body-font" style={{ color: "rgba(240,237,232,0.5)", fontSize: 13, textDecoration: "none" }}>Google Play (Android)</a>
                </div>
              </div>
            </div>
          </div>
          {/* Bottom row */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <span className="body-font" style={{ fontSize: 12, color: "rgba(240,237,232,0.2)", fontWeight: 300 }}>
              © 2026 SatStacker. Not financial advice. Bitcoin only.
            </span>
            <div style={{ display: "flex", gap: 20 }}>
              <a href="/privacy" className="body-font" style={{ color: "rgba(240,237,232,0.3)", fontSize: 12, textDecoration: "none" }}>Privacy Policy</a>
              <a href="/terms" className="body-font" style={{ color: "rgba(240,237,232,0.3)", fontSize: 12, textDecoration: "none" }}>Terms</a>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
}